<script>
import * as m from '@app/paraglide/messages.js';
import {
    modalState, hideModal
} from '@app/store/modalStore.svelte.js';
import { toast } from '@zerodevx/svelte-toast';
import {
    eventGroupState
} from '@app/store/eventGroupStore.svelte.js';
import {
    projectState
} from '@app/store/projectStore.svelte.js';
import {
    editorState
} from "@app/store/editorStore.svelte.js";
import {
    insertHistoryAction
} from '@app/store/historyStore.svelte.js';
import {
    environment
} from '@app/store/envStore.svelte.js';
import {
    fade
} from 'svelte/transition';
/* Firebase */
import firebase from '@app/configs/firebase.js';
import 'firebase/compat/functions';

/* CC LIB */
import convertToPlainText from '@cclib/functions/quill/convertToPlainText.js';
import tcLib from '@cclib/lib/timecode.js';

import { assignEventAudio } from '@app/lib/audio/assignEventAudio.js';
import {
    renderParentAudio,
    getAudioDurationFromUrl,
    MAX_TTS_CHARS,
} from '@app/lib/audio/parentTts.js';

// Render one cue through the embedding parent host (e.g. Unity) local TTS,
// normalizing the response to the shape the batch loop already expects.
async function renderEventViaParent(event, plainText) {
    if (plainText.length > MAX_TTS_CHARS) {
        throw new Error(`Text exceeds the ${MAX_TTS_CHARS} character limit for local text-to-speech.`);
    }

    const result = await renderParentAudio({
        text: plainText,
        voice: event.voice.id,
        speed: parseFloat(event.rate),
        format: "mp3",
        eventId: event.id,
    });

    if (!result || result.success === false || !result.url) {
        throw new Error(result?.message || "No audio returned from local text-to-speech.");
    }

    const durationSec = await getAudioDurationFromUrl(result.url);
    return {
        data: {
            eventId: result.eventId || event.id,
            audio: result.url,
            duration: durationSec * 1000,
        },
    };
}

let progress = $state(0),
    statusMsg = $state(m.render_audio_starting_status()),
    queueSize = 10,
    eventQueue,
    selectedEventCache = [...eventGroupState[projectState.selected].selected],
    totalEvents = $state(selectedEventCache.length);

async function beginProcessing() {
    toast.push(m.render_audio_converting_toast(), {classes: ['toast-info']});

    if (eventGroupState[projectState.selected] && eventGroupState[projectState.selected].type !== "audio description") {
        toast.push(m.render_audio_must_be_ad_toast(), {classes: ['toast-danger']});
        hideModal();
    }

    try {
        eventGroupState[projectState.selected].selected = [];
        eventQueue = JSON.parse(JSON.stringify(eventGroupState[projectState.selected].events.filter((event, index) => {
            return selectedEventCache.indexOf(index) > -1
        })));

        statusMsg = m.render_audio_processing_queue_status();
        let elevenLabsEventIndex = eventQueue.findIndex((evt) => {
            return evt.voice.provider === "ElevenLabs"
        });

        if (elevenLabsEventIndex > -1) {
            renderAudio(eventQueue.splice(0, eventQueue.length > 2 ? 2 : eventQueue.length));
        } else {
            renderAudio(eventQueue.splice(0, eventQueue.length > queueSize ? queueSize : eventQueue.length));
        }

    } catch (err) {
        console.log(err, err.message);
        toast.push(m.render_audio_failed_toast({error: err.message}), {classes: ['toast-danger']});
        hideModal();
    }
}

function renderAudio(queue) {
    try {
        progress = (((totalEvents - eventQueue.length) / totalEvents) * 100).toFixed(2);
        statusMsg = m.render_audio_rendering_status({start: totalEvents-eventQueue.length, end: Math.min(totalEvents-eventQueue.length+(queueSize-1), totalEvents), total: totalEvents});
        let renderTasks = [];
        
        queue.forEach((event) => {
            // Check if this event has a user recording (audioFileUuid exists)
            if (event.audioFileUuid) {
                // Get updated download link for user recording
                const userId = firebase.auth().currentUser.uid;
                const storagePath = `users/${userId}/recordings/${event.audioFileUuid}.mp3`;
                
                renderTasks.push(
                    firebase.functions().httpsCallable('v8GetDownloadLink')(storagePath)
                        .then(result => {
                            return {
                                data: {
                                    eventId: event.id,
                                    audio: result.data[0],
                                    duration: event.audioFileDuration ? event.audioFileDuration * 1000 : 0,
                                    isRecording: true
                                }
                            };
                        })
                );
            } else {
                // Original TTS rendering logic
                let plainText = convertToPlainText(event.text, " ");
                if (plainText) {
                    try {
                        if (environment.adLicense) {
                            // Route through the parent host's local TTS.
                            renderTasks.push(renderEventViaParent(event, plainText));
                        } else {
                            renderTasks.push(firebase.functions().httpsCallable('v8TextToSpeechProviderV4')({
                                provider: event.voice.provider,
                                language: event.voice.language,
                                voice: event.voice.name,
                                voice_id: event.voice.id,
                                stability: event.voiceStability / 100,
                                similarity: event.voiceSimilarity / 100,
                                speed: parseFloat(event.rate),
                                style: event.speakingStyle,
                                audioEncoding: "MP3",
                                text: plainText,
                                eventGroupId: eventGroupState[projectState.selected].id,
                                eventId: event.id
                            }));
                        }
                    } catch (err) {
                        console.log("ERROR - FAILED CALL TO RENDER CLOUD");
                        console.log(err, err.message);
                        toast.push(m.render_audio_failed_toast({error: err.message}), {classes: ['toast-danger']});
                        hideModal();
                    }
                }
            }
        });

        Promise.allSettled(renderTasks).then((results) => {
            results.forEach(res => {
                if (res.status === "rejected") {
                    console.log(res.reason);
                    toast.push(m.render_audio_failed_toast({error: res.reason?.message || res.reason}), {classes: ['toast-danger']});
                    return;
                }

                if (!res.value) {
                    return;
                }

                console.log(res);

                let eventIndex = eventGroupState[projectState.selected].events.findIndex(event => {
                    return event.id === res.value.data.eventId;
                })

                const targetEvent = eventGroupState[projectState.selected].events[eventIndex];

                if (res.value.data.isRecording) {
                    // Recording link refresh: keep existing duration, just rotate the URL.
                    assignEventAudio(targetEvent, { audioFile: res.value.data.audio });
                } else {
                    // Check if duration is a number or string (ms )
                    let durationSec = typeof res.value.data.duration === 'number' ? res.value.data.duration/1000 : tcLib.tcMsToSec(res.value.data.duration);

                    assignEventAudio(targetEvent, {
                        audioFile: res.value.data.audio,
                        audioFileDuration: durationSec
                    });

                    if (editorState.autoTrimEvent && !isNaN(targetEvent.start && durationSec > 0)){
                        targetEvent.end = targetEvent.start + durationSec + 0.5;
                    }
                }
            });

            if (eventQueue.length > 0) {
                let elevenLabsEventIndex = eventQueue.findIndex((evt) => {
                    return evt.voice.provider === "ElevenLabs"
                });

                if (elevenLabsEventIndex > -1) {
                    renderAudio(eventQueue.splice(0, eventQueue.length > 2 ? 2 : eventQueue.length));
                } else {
                    renderAudio(eventQueue.splice(0, eventQueue.length > queueSize ? queueSize : eventQueue.length));
                }
            } else {
                finishProcess();
            }
        }).catch(err => {
            console.log(err, err.message);
            toast.push(m.render_audio_failed_retry_toast({error: err.message}), {classes: ['toast-danger']});
            hideModal();
        });
    } catch (err) {
        console.log(err, err.message);
        toast.push(m.render_audio_failed_toast({error: err.message}), {classes: ['toast-danger']});
        hideModal();
    }
}

function finishProcess() {
    insertHistoryAction({
        name: m.render_audio_action_name(), //action name
        eventGroup: projectState.selected,
        snapshots: [{
            store: "eventGroupState",
            value: JSON.stringify(eventGroupState)
        }]
    });

    toast.push(m.render_audio_complete_toast(), {classes: ['toast-success']});
    eventGroupState[projectState.selected].selected = selectedEventCache;
    hideModal();
}

beginProcessing();
</script>

<div transition:fade={{duration: 100}} class="modal {modalState.selected === 'renderAudio' ? 'show d-block' : ''}" role="dialog" aria-labelledby="renderAudioTitle" tabindex="-1" id="RenderAudioModal">
    <div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable" role="document">
        <div class="modal-content">
            <div class="modal-header">
                <h4 class="modal-title" id="renderAudioTitle">{m.render_audio_title()}</h4>
            </div>
            <div class="modal-body">
                <h5>{totalEvents ? m.render_audio_rendering_events({count: totalEvents}) : m.render_audio_rendering_no_events()}</h5>
                <p><i class="bi bi-exclamation-diamond-fill" role="img" aria-hidden="true"></i> {m.render_audio_leave_open_text()}</p>
                <div class="progress" role="progressbar" aria-label={m.render_audio_progress_aria_label()} aria-valuenow={progress} aria-valuemin="0" aria-valuemax="100">
                    <div class="progress-bar bg-primary progress-bar-striped progress-bar-animated" style="width: {progress}%;">{progress}%</div>
                </div>
                <p class="text-center text-muted small" aria-live="polite">{statusMsg}</p>
            </div>
        </div>
    </div>
</div>
